import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { useDealStatusContext } from './contexts/DealStatusContext';
import { IDealsPayload } from '@/creditflowresearch/domain/vo/IDeal';
import { ICorporatePayload } from '@/creditflowresearch/application/Deal/types';
import { useProductsContext } from '@/creditflowresearch/application/Deal/contexts/ProductsContext';
import { useHeaderMenuContext } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import Region from '@/creditflowresearch/domain/Region';

export interface IUseDealFiltersProps<P extends IDealsPayload | ICorporatePayload> {
  additionalFilters?: P,
  pageFilters?: P | null,
}

export const useDealFilters = <
  P extends IDealsPayload | ICorporatePayload,
>({ additionalFilters, pageFilters }: IUseDealFiltersProps<P>) => {
  const { selectedStatuses } = useDealStatusContext();
  const { activeProduct, selectedTab } = useProductsContext();
  const router = useRouter();
  const { regions } = useHeaderMenuContext();

  const buildFilters = useCallback(() => {
    const baseFilters = pageFilters?.filters || {};
    const regionsQueryString = (router.query.regions as string) || '';
    const hasAdditionalFilters = additionalFilters && Object.keys(additionalFilters).length > 0;

    const getRegionIds = (): string[] => {
      if (regions && regionsQueryString) {
        return Region.getIds(regionsQueryString);
      }
      return (baseFilters as { includedRegionIds?: string[] })?.includedRegionIds || [];
    };

    const regionIds = getRegionIds();

    if (hasAdditionalFilters || Object.keys(additionalFilters?.filters || {}).length > 0) {
      return {
        ...baseFilters,
        dateFrom: additionalFilters.filters?.dateFrom,
        dateTo: additionalFilters.filters?.dateTo,
        includedSectorId: (additionalFilters as IDealsPayload).filters?.includedSectorId,
        includedSubsectorId: (additionalFilters as IDealsPayload).filters?.includedSubsectorId,
        includedIndustryIds: (additionalFilters as ICorporatePayload).filters?.includedIndustryIds,
        includedSubindustryIds: (additionalFilters as ICorporatePayload).filters?.includedSubindustryIds,
        includedProductIds: additionalFilters.filters?.includedProductIds?.length
          ? additionalFilters.filters.includedProductIds
          : activeProduct,
        includedRegionIds: additionalFilters.filters?.includedRegionIds,
        includedUnderwriterIds: (additionalFilters as ICorporatePayload).filters?.includedUnderwriterIds,
        includedRatingAgencyIds: (additionalFilters as ICorporatePayload).filters?.includedRatingAgencyIds,
        includedStatusGroups: [],
      };
    }

    const ssrProductIds = (baseFilters as { includedProductIds?: string[] }).includedProductIds || [];
    const productIds = selectedTab?.productIds?.length ? selectedTab.productIds : ssrProductIds;

    return {
      ...baseFilters,
      includedStatusGroups: selectedStatuses,
      includedProductIds: productIds,
      includedRegionIds: regionIds,
    };
  }, [pageFilters, additionalFilters, selectedStatuses, selectedTab, router.query, regions]);

  return {
    buildFilters,
    selectedStatuses,
    activeProduct,
  };
};
